import { GlobalStats, UseCaseUsageStats } from "lib/models/user-insights-models";
import { UserInsightsService } from "lib/services/user-insights-service";
import { useEffect, useMemo, useRef, useState } from "react";

interface UseUseCaseInsightsResult {
  statsMap: Record<string, UseCaseUsageStats>;
  globalStats: GlobalStats | null;
  loadingGlobal: boolean;
}

export function useUseCaseInsights(useCaseIds: string[]): UseUseCaseInsightsResult {
  const [statsMap, setStatsMap] = useState<Record<string, UseCaseUsageStats>>({});
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [loadingGlobal, setLoadingGlobal] = useState(true);
  const service = useMemo(() => new UserInsightsService(), []);
  const fetchedIdsRef = useRef<Set<string>>(new Set());

  // Fetch global stats once
  useEffect(() => {
    service
      .getGlobalStats()
      .then(setGlobalStats)
      .catch(() => setGlobalStats(null))
      .finally(() => setLoadingGlobal(false));
  }, []);

  // Fetch batch stats when new IDs appear (for detail/dashboard pages)
  useEffect(() => {
    const newIds = useCaseIds.filter((id) => id && !fetchedIdsRef.current.has(id));
    if (!newIds.length) return;

    newIds.forEach((id) => fetchedIdsRef.current.add(id));

    service
      .getBatchStats(newIds)
      .then((response) => {
        setStatsMap((prev) => ({ ...prev, ...response.stats }));
      })
      .catch(() => {});
  }, [useCaseIds.join(",")]);

  return { statsMap, globalStats, loadingGlobal };
}
